import React, { useState, useEffect, useMemo, useRef } from "react";
import { db } from "../../firebase";
import { Form, Table, Modal, Row, Col, Button } from "react-bootstrap";
import { CSVLink } from "react-csv";
import { toast } from "react-toastify";
import moment from "moment";
import styles from "./PullReport.module.scss";
import { parseReportForMeasurements, htmlToRawText } from "../../utils/measurementReportParser";
import { formatWeightDisplay } from "../../utils/helpers";
import { formatMeasurementFeetInches } from "../../utils/feetInches";

export default function PullReport({ reportType = "General", pageTitle }) {
  const title = pageTitle || `Pull Report (${reportType})`;
  const typeLower = (reportType || "general").toLowerCase();

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewFilter, setPreviewFilter] = useState("");
  const [fetchError, setFetchError] = useState(null);
  const [playerNamesMap, setPlayerNamesMap] = useState({});
  const [playerDOBMap, setPlayerDOBMap] = useState({});
  const [selectedReport, setSelectedReport] = useState(null);
  const [parserResults, setParserResults] = useState(null);
  const [showParserModal, setShowParserModal] = useState(false);
  const reportContentRef = useRef(null);

  useEffect(() => {
    const fetchReports = async () => {
      setLoading(true);
      setFetchError(null);
      try {
        const snapshot = await db.collection("reports").get();
        const allDocs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        const data = allDocs.filter(
          (r) => (r.type || "").toLowerCase() === typeLower
        );
        data.sort((a, b) => {
          const dateA = a.reportDate ? (a.reportDate.toDate ? a.reportDate.toDate() : new Date(a.reportDate)) : new Date(0);
          const dateB = b.reportDate ? (b.reportDate.toDate ? b.reportDate.toDate() : new Date(b.reportDate)) : new Date(0);
          return dateB - dateA;
        });
        setReports(data);

        const playerIds = [...new Set(data.map((r) => r.playerProfile).filter(Boolean))];
        if (playerIds.length > 0) {
          const names = {};
          const dobs = {};
          await Promise.all(
            playerIds.map(async (pid) => {
              try {
                const doc = await db.collection("player-profile").doc(pid).get();
                if (doc.exists) {
                  const d = doc.data();
                  names[pid] = [d.FirstName, d.LastName].filter(Boolean).join(" ").trim() || pid;
                  const dobVal = d.DOB ?? d.dob;
                  if (dobVal) dobs[pid] = dobVal;
                } else {
                  names[pid] = pid;
                }
              } catch {
                names[pid] = pid;
              }
            })
          );
          setPlayerNamesMap(names);
          setPlayerDOBMap(dobs);
        } else {
          setPlayerNamesMap({});
          setPlayerDOBMap({});
        }
      } catch (error) {
        console.error(`Error fetching ${reportType} reports:`, error);
        setFetchError(error.message);
        toast.error("Failed to load reports. Check console for details.");
        setReports([]);
      } finally {
        setLoading(false);
      }
    };
    fetchReports();
  }, [typeLower, reportType]);

  const rawContentMap = useMemo(() => {
    const m = {};
    reports.forEach((r) => {
      m[r.id] = htmlToRawText(r.reportContent || "");
    });
    return m;
  }, [reports]);

  const filteredReports = useMemo(() => {
    if (!previewFilter.trim()) return reports;
    const term = previewFilter.trim().toLowerCase();
    return reports.filter(
      (r) =>
        (r.reportPreview && r.reportPreview.toLowerCase().includes(term)) ||
        (r.location && r.location.toLowerCase().includes(term)) ||
        (rawContentMap[r.id] && rawContentMap[r.id].toLowerCase().includes(term))
    );
  }, [reports, previewFilter, rawContentMap]);

  const csvData = useMemo(() => {
    return filteredReports.map((r) => ({
      "Report Date": r.reportDate ? moment(r.reportDate.toDate ? r.reportDate.toDate() : r.reportDate).format("YYYY-MM-DD") : "",
      Type: r.type || "",
      Preview: r.reportPreview || "",
      ReportContent: r.reportContent || "",
      Location: r.location || "",
      Author: r.creator || "",
      "Player Profile ID": r.playerProfile || "",
      "URL Reference": r.typeSpecificFields?.urlReference || "",
    }));
  }, [filteredReports, playerNamesMap]);

  useEffect(() => {
    if (!selectedReport || !reportContentRef.current) return;
    if (selectedReport.reportContent) {
      reportContentRef.current.innerHTML = selectedReport.reportContent;
    }
    return () => {
      if (reportContentRef.current) reportContentRef.current.innerHTML = "";
    };
  }, [selectedReport]);

  const handleCloseModal = () => setSelectedReport(null);

  const runParser = () => {
    if (filteredReports.length === 0) {
      toast.info("No reports to parse. Apply a filter or ensure reports are loaded.");
      return;
    }
    const parsed = filteredReports.flatMap((r) =>
      parseReportForMeasurements(
        r,
        playerNamesMap[r.playerProfile] || r.playerProfile || "",
        playerDOBMap[r.playerProfile]
      )
    );
    setParserResults(parsed);
    setShowParserModal(true);
    toast.success(`Parsed ${parsed.length} row(s) from ${filteredReports.length} report(s).`);
  };

  if (loading) {
    return (
      <div className={styles.wrapper}>
        <h1 className={styles.title}>{title}</h1>
        <p>Loading reports...</p>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h1 className={styles.title}>{title}</h1>
        <div className={styles.actions}>
          <Form.Control
            type="text"
            placeholder={typeLower === "scouting" ? "Search preview, location, or content (e.g. Height w/o shoes:)" : "Search by preview, location, or content..."}
            value={previewFilter}
            onChange={(e) => setPreviewFilter(e.target.value)}
            className={styles.searchInput}
          />
          <Button variant="outline-dark" onClick={runParser}>
            Parser
          </Button>
          <CSVLink
            data={csvData}
            filename={`${typeLower}-reports-${moment().format("YYYY-MM-DD")}.csv`}
            className="btn btn-dark"
          >
            Download CSV
          </CSVLink>
        </div>
      </div>

      {fetchError && (
        <div className="alert alert-danger" role="alert">
          Could not load reports: {fetchError}
        </div>
      )}

      <div className={styles.resultCount}>
        Showing {filteredReports.length} of {reports.length} report{reports.length !== 1 ? "s" : ""}
      </div>

      <div className={styles.tableWrap}>
        <Table bordered hover responsive className={styles.table}>
          <thead>
            <tr>
              <th>Report Date</th>
              <th>Type</th>
              <th>Preview</th>
              <th>reportContent</th>
              <th>Location</th>
              <th>Author</th>
              <th>Player ID</th>
            </tr>
          </thead>
          <tbody>
            {filteredReports.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center text-muted">
                  {reports.length === 0
                    ? `No ${reportType} reports found. Only reports with type = ${reportType} are shown.`
                    : "No reports match your search."}
                </td>
              </tr>
            ) : (
              filteredReports.map((r) => (
                <tr
                  key={r.id}
                  className={styles.clickableRow}
                  onClick={() => setSelectedReport(r)}
                >
                  <td>
                    {r.reportDate
                      ? moment(r.reportDate.toDate ? r.reportDate.toDate() : r.reportDate).format("MM/DD/YYYY")
                      : "--"}
                  </td>
                  <td>{r.type || "--"}</td>
                  <td>{r.reportPreview || "--"}</td>
                  <td>{r.reportContent || "--"}</td>
                  <td>{r.location || "--"}</td>
                  <td>{r.creator || "--"}</td>
                  <td>{r.playerProfile || "--"}</td>
                  
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <Modal show={!!selectedReport} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Report</Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          {selectedReport && (
            <>
              <Row>
                <Col md={12}>
                  <p><b>Date: </b>{selectedReport.reportDate ? moment(selectedReport.reportDate.toDate ? selectedReport.reportDate.toDate() : selectedReport.reportDate).format("MM/DD/YYYY") : "--"}</p>
                  <p><b>Type: </b>{selectedReport.type || "--"}</p>
                  <p><b>Title: </b>{selectedReport.reportPreview || "--"}</p>
                  {selectedReport.location && <p><b>Location: </b>{selectedReport.location}</p>}
                  {selectedReport.creator && <p><b>Author: </b>{selectedReport.creator}</p>}
                  <p><b>Player: </b>{playerNamesMap[selectedReport.playerProfile] || selectedReport.playerProfile || "--"}</p>
                  {selectedReport.typeSpecificFields?.urlReference && (
                    <p>
                      <b>URL Reference: </b>{" "}
                      <a href={selectedReport.typeSpecificFields.urlReference} target="_blank" rel="noopener noreferrer">
                        {selectedReport.typeSpecificFields.urlReference}
                      </a>
                    </p>
                  )}
                </Col>
              </Row>
              {selectedReport.reportContent && (
                <>
                  <Row className="mt-2">
                    <Col md={12} className="d-flex flex-wrap gap-2">
                      <Button
                        variant="outline-dark"
                        size="sm"
                        onClick={() => {
                          const raw = htmlToRawText(selectedReport.reportContent);
                          navigator.clipboard.writeText(raw).then(
                            () => toast.success("Content copied to clipboard. Paste into Google Sheets."),
                            () => toast.error("Copy failed.")
                          );
                        }}
                      >
                        Copy content
                      </Button>
                      <Button
                        variant="outline-dark"
                        size="sm"
                        onClick={() => {
                          const parsed = parseReportForMeasurements(
                            selectedReport,
                            playerNamesMap[selectedReport.playerProfile] || selectedReport.playerProfile || "",
                            playerDOBMap[selectedReport.playerProfile]
                          );
                          if (!parsed.length) {
                            toast.info("No measurement data parsed from this report.");
                            return;
                          }
                          const headers = [
                            "Player ID", "Player Name", "Age", "Event", "Event Date", "Body Fat %",
                            "Hand L", "Hand W", "Height w/o shoes", "Height w/ shoes", "Lane Agility",
                            "Shuttle", "Sprint", "Standing Reach", "Vert Max", "Vert No Step",
                            "Weight", "Wingspan", "Raw data"
                          ];
                          const escape = (v) => String(v ?? "").replace(/\t/g, " ").replace(/\r?\n/g, " ");
                          const headerRow = headers.join("\t");
                          const dataRows = parsed.map((r) =>
                            [
                              r.playerProfileID, r.playerName, r.age, r.event, r.eventDate, r.bodyFat,
                              r.handL, r.handW, r.heightNone, r.heightShoes, r.laneAgility,
                              r.shuttle, r.sprint, r.standingReach, r.vertMax, r.vertNoStep,
                              r.weight, r.wingspan, r.rawData
                            ].map(escape).join("\t")
                          );
                          const tsv = [headerRow, ...dataRows].join("\n");
                          navigator.clipboard.writeText(tsv).then(
                            () => toast.success(`Parsed ${parsed.length} row(s) copied. Paste into Google Sheets.`),
                            () => toast.error("Copy failed.")
                          );
                        }}
                      >
                        Copy parsed measurements
                      </Button>
                    </Col>
                  </Row>
                  <Row className="mt-3">
                    <Col md={12}>
                      <p><b>Report content:</b></p>
                      <div ref={reportContentRef} className={styles.reportContent} />
                    </Col>
                  </Row>
                </>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={showParserModal} onHide={() => setShowParserModal(false)} size="xl" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Parsed measurement data ({reportType})</Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.parserModalBody}>
          {parserResults && parserResults.length > 0 && (
            <>
              <p className="text-muted small mb-2">
                Extracted from {parserResults.length} report(s). Raw data is human-readable text from report content (HTML stripped).
              </p>
              <div className={styles.parserTableWrap}>
                <Table bordered hover responsive size="sm" className={styles.parserTable}>
                  <thead>
                    <tr>
                      <th>Player ID</th>
                      <th>Player Name</th>
                      <th>Age</th>
                      <th>Event</th>
                      <th>Event Date</th>
                      <th>Body Fat %</th>
                      <th>Hand L</th>
                      <th>Hand W</th>
                      <th>Height w/o shoes</th>
                      <th>Height w/ shoes</th>
                      <th>Lane Agility</th>
                      <th>Shuttle</th>
                      <th>Sprint</th>
                      <th>Standing Reach</th>
                      <th>Vert Max</th>
                      <th>Vert No Step</th>
                      <th>Weight</th>
                      <th>Wingspan</th>
                      <th>Raw data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parserResults.map((row, idx) => (
                      <tr key={idx}>
                        <td>{row.playerProfileID || "--"}</td>
                        <td>{row.playerName || "--"}</td>
                        <td>{row.age || "--"}</td>
                        <td className={styles.parserCellWrap}>{row.event || "--"}</td>
                        <td>{row.eventDate || "--"}</td>
                        <td>{row.bodyFat || "--"}</td>
                        <td>{row.handL || "--"}</td>
                        <td>{row.handW || "--"}</td>
                        <td>{formatMeasurementFeetInches(row.heightNone)}</td>
                        <td>{formatMeasurementFeetInches(row.heightShoes)}</td>
                        <td>{row.laneAgility || "--"}</td>
                        <td>{row.shuttle || "--"}</td>
                        <td>{row.sprint || "--"}</td>
                        <td>{formatMeasurementFeetInches(row.standingReach)}</td>
                        <td>{row.vertMax || "--"}</td>
                        <td>{row.vertNoStep || "--"}</td>
                        <td>{formatWeightDisplay(row.weight)}</td>
                        <td>{formatMeasurementFeetInches(row.wingspan)}</td>
                        <td className={styles.parserCellWrap}>{row.rawData || "--"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <div className="mt-2 d-flex flex-wrap gap-2 align-items-center">
                <Button
                  variant="outline-dark"
                  size="sm"
                  onClick={() => {
                    const headers = [
                      "Player ID", "Player Name", "Age", "Event", "Event Date", "Body Fat %",
                      "Hand L", "Hand W", "Height w/o shoes", "Height w/ shoes", "Lane Agility",
                      "Shuttle", "Sprint", "Standing Reach", "Vert Max", "Vert No Step",
                      "Weight", "Wingspan", "Raw data"
                    ];
                    const escape = (v) => String(v ?? "").replace(/\t/g, " ").replace(/\r?\n/g, " ");
                    const headerRow = headers.join("\t");
                    const dataRows = parserResults.map((r) =>
                      [
                        r.playerProfileID, r.playerName, r.age, r.event, r.eventDate, r.bodyFat,
                        r.handL, r.handW, r.heightNone, r.heightShoes, r.laneAgility,
                        r.shuttle, r.sprint, r.standingReach, r.vertMax, r.vertNoStep,
                        r.weight, r.wingspan, r.rawData
                      ].map(escape).join("\t")
                    );
                    const tsv = [headerRow, ...dataRows].join("\n");
                    navigator.clipboard.writeText(tsv).then(
                      () => toast.success("Table copied to clipboard. Paste into Google Sheets or Excel."),
                      () => toast.error("Copy failed.")
                    );
                  }}
                >
                  Copy table (paste into Sheets)
                </Button>
                <CSVLink
                  data={parserResults.map((r) => ({
                    playerProfileID: r.playerProfileID,
                    playerName: r.playerName,
                    age: r.age,
                    event: r.event,
                    eventDate: r.eventDate,
                    bodyFat: r.bodyFat,
                    handL: r.handL,
                    handW: r.handW,
                    heightNone: r.heightNone,
                    heightShoes: r.heightShoes,
                    laneAgility: r.laneAgility,
                    shuttle: r.shuttle,
                    sprint: r.sprint,
                    standingReach: r.standingReach,
                    vertMax: r.vertMax,
                    vertNoStep: r.vertNoStep,
                    weight: r.weight,
                    wingspan: r.wingspan,
                    rawData: r.rawData,
                  }))}
                  filename={`parsed-measurements-${moment().format("YYYY-MM-DD-HHmm")}.csv`}
                  separator="$"
                  className="btn btn-dark btn-sm"
                >
                  Download parsed ($ delimiter)
                </CSVLink>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}
